import { CircleAlert, Clock, XIcon } from 'lucide-react'
import { useRef, useState, type KeyboardEvent } from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'

import {
  Badge,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Label,
  Checkbox,
  MultiSelect,
  SingleSelect,
} from '@fp/ui-kit'
import { cn } from '@lib/cn'
import { DateSinglePicker } from '@ui/DateSinglePicker'
import { ShowInfoPopover } from '@ui/ShowInfoPopover'

export type SelectOption = {
  label: string
  value: string
}

export type BaseFieldProps<T extends FieldValues> = {
  name: Path<T>
  label: string
  control: Control<T>
  error?: string
  disabled?: boolean
  required?: boolean
  touched?: boolean
  infoText?: string
  /** Highlight the field (e.g. when previewing a scheduled change). */
  highlight?: boolean
}

const HIGHLIGHT_CONTROL_CLASS = 'outline outline-2 outline-primary rounded-md'
const HIGHLIGHT_LABEL_CLASS = 'text-primary'

const RequiredIndicator = () => <span className='text-destructive-foreground ml-0.5 font-semibold'>*</span>

const ErrorIndicator = ({ error }: { error?: string }) =>
  error ? (
    <Tooltip defaultOpen={Boolean(error)}>
      <TooltipTrigger asChild>
        <button
          type='button'
          aria-label={error}
          className='inline-flex h-3 w-3 items-center justify-center rounded-full text-destructive-foreground outline-none focus-visible:ring-2 focus-visible:ring-destructive-foreground/30'
        >
          <CircleAlert className=' min-h-4! min-w-4!' />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side='top'
        className='text-base max-w-72! rounded-lg! border border-destructive/20! bg-destructive! px-2 py-1 text-left text-destructive-foreground'
        arrowClassName='bg-destructive! fill-destructive!'
      >
        {error}
      </TooltipContent>
    </Tooltip>
  ) : null

const FieldLabel = <T extends FieldValues>({
  name,
  label,
  required,
  infoText,
  error,
  highlight,
}: {
  name: Path<T>
  label: string
  required?: boolean
  infoText?: string
  error?: string
  highlight?: boolean
}) => (
  <div className='flex gap-2 items-center'>
    <Label htmlFor={String(name)} className={cn('flex items-center gap-1', highlight && HIGHLIGHT_LABEL_CLASS)}>
      {/* A highlighted field is one the previewed schedule rewrites — flag it as pending. */}
      {highlight && <Clock size={12} aria-label='Pending scheduled change' />}
      {label}
      {required && <RequiredIndicator />}
    </Label>
    {infoText && <ShowInfoPopover text={infoText} />}
    <ErrorIndicator error={error} />
  </div>
)

const FormFieldContainer = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={cn('grid gap-2 *:min-w-0', className)}>{children}</div>
)

const useShowError = (touched?: boolean, error?: string) => {
  if (!error) return false

  // Some form consumers don't pass `touched`; in that case rely on RHF validation mode (error presence).
  if (typeof touched === 'undefined') return true

  return Boolean(touched)
}

type TextInputFieldProps<T extends FieldValues> = BaseFieldProps<T> & {
  type?: 'text' | 'email' | 'number' | 'password'
  placeholder?: string
  min?: number
  max?: number
  step?: number | string
  /** Native autocomplete hint. Pass 'off' / 'new-password' to suppress browser autofill. */
  autoComplete?: string
}

type NumberInputFieldProps<T extends FieldValues> = Omit<TextInputFieldProps<T>, 'type'>

export function TextInputField<T extends FieldValues>({
  name,
  label,
  control,
  error,
  disabled = false,
  required = false,
  touched,
  type = 'text',
  placeholder,
  min,
  max,
  step,
  infoText,
  highlight,
  autoComplete,
}: TextInputFieldProps<T>) {
  const showError = useShowError(touched, error)

  return (
    <FormFieldContainer>
      <FieldLabel
        name={name}
        label={label}
        required={required}
        infoText={infoText}
        error={showError ? error : undefined}
        highlight={highlight}
      />
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Input
            id={String(name)}
            type={type}
            placeholder={placeholder}
            disabled={disabled}
            min={min}
            max={max}
            step={step}
            autoComplete={autoComplete}
            aria-invalid={showError}
            className={cn(
              showError && 'border-destructive-foreground focus-visible:border-destructive-foreground',
              highlight && HIGHLIGHT_CONTROL_CLASS,
            )}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              const value = type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value
              field.onChange(value)
            }}
          />
        )}
      />
    </FormFieldContainer>
  )
}

export function NumberInputField<T extends FieldValues>(props: NumberInputFieldProps<T>) {
  return <TextInputField {...props} type='number' />
}

type SingleSelectFieldProps<T extends FieldValues> = BaseFieldProps<T> & {
  options: SelectOption[]
  placeholder?: string
  isToggleEnabled?: boolean
  modal?: boolean
}

export function SingleSelectField<T extends FieldValues>({
  name,
  label,
  control,
  error,
  disabled = false,
  required = false,
  touched,
  options,
  placeholder,
  infoText,
  isToggleEnabled,
  modal,
  highlight,
}: SingleSelectFieldProps<T>) {
  const showError = useShowError(touched, error)

  return (
    <FormFieldContainer>
      <FieldLabel
        name={name}
        label={label}
        required={required}
        infoText={infoText}
        error={showError ? error : undefined}
        highlight={highlight}
      />
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <SingleSelect
            options={options}
            value={field.value ?? ''}
            onChange={field.onChange}
            placeholder={placeholder}
            disabled={disabled}
            isToggleEnabled={isToggleEnabled}
            modal={modal}
            selectClassName={cn(showError && 'border-destructive-foreground', highlight && HIGHLIGHT_CONTROL_CLASS)}
          />
        )}
      />
    </FormFieldContainer>
  )
}

type MultiSelectFieldProps<T extends FieldValues> = BaseFieldProps<T> & {
  options: SelectOption[]
  placeholder?: string
  modal?: boolean
}

export function MultiSelectField<T extends FieldValues>({
  name,
  label,
  control,
  error,
  disabled = false,
  required = false,
  touched,
  options,
  placeholder = 'All',
  modal = true,
  infoText,
  highlight,
}: MultiSelectFieldProps<T>) {
  const showError = useShowError(touched, error)

  return (
    <FormFieldContainer>
      <FieldLabel
        name={name}
        label={label}
        required={required}
        infoText={infoText}
        error={showError ? error : undefined}
        highlight={highlight}
      />
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <MultiSelect
            options={options}
            value={(field.value as string[]) ?? []}
            onValueChange={field.onChange}
            modal={modal}
            placeholder={placeholder}
            disabled={disabled}
            triggerClassName={cn(showError && 'border-destructive-foreground', highlight && HIGHLIGHT_CONTROL_CLASS)}
          />
        )}
      />
    </FormFieldContainer>
  )
}

type ChipsInputProps = {
  value: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  id?: string
  invalid?: boolean
  highlight?: boolean
}

const ChipsInput = ({ value, onChange, placeholder, id, invalid, highlight }: ChipsInputProps) => {
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const add = () => {
    const entry = draft.trim()
    if (entry && !value.includes(entry)) onChange([...value, entry])
    setDraft('')
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      add()
    } else if (event.key === 'Backspace' && !draft && value.length) {
      onChange(value.slice(0, -1))
    }
  }

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className={cn(
        'flex min-h-9.5 flex-wrap items-center gap-1 rounded-lg border border-input bg-card px-2 py-1.5',
        'focus-within:border-ring overflow-scroll',
        invalid && 'border-destructive-foreground focus-within:border-destructive-foreground',
        highlight && HIGHLIGHT_CONTROL_CLASS,
      )}
      role='button'
      tabIndex={0}
    >
      {value.map((entry) => (
        <Badge key={entry} variant='outline' className='pr-1.5'>
          {entry}

          <span
            role='button'
            className='cursor-pointer ml-1 text-muted-foreground hover:text-foreground'
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation()
              onChange(value.filter((item) => item !== entry))
            }}
          >
            <XIcon size={12} />
          </span>
        </Badge>
      ))}

      <Input
        ref={inputRef}
        id={id}
        variant='minimal'
        value={draft}
        placeholder={value.length ? '' : placeholder}
        className='h-6 flex-1 basis-24'
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={add}
      />
    </div>
  )
}

type ChipsInputFieldProps<T extends FieldValues> = BaseFieldProps<T> & { placeholder?: string }

export function ChipsInputField<T extends FieldValues>({
  name,
  label,
  control,
  error,
  required = false,
  touched,
  infoText,
  highlight,
  placeholder,
}: ChipsInputFieldProps<T>) {
  const showError = useShowError(touched, error)

  return (
    <FormFieldContainer>
      <FieldLabel
        name={name}
        label={label}
        required={required}
        infoText={infoText}
        error={showError ? error : undefined}
        highlight={highlight}
      />
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <ChipsInput
            id={String(name)}
            value={(field.value as string[]) ?? []}
            onChange={field.onChange}
            placeholder={placeholder}
            invalid={showError}
            highlight={highlight}
          />
        )}
      />
    </FormFieldContainer>
  )
}

type DatePickerFieldProps<T extends FieldValues> = BaseFieldProps<T> & {
  disableFutureDates?: boolean
  disablePastDates?: boolean
  isFullWidth?: boolean
}

export function DatePickerField<T extends FieldValues>({
  name,
  label,
  control,
  error,
  disabled = false,
  required = false,
  touched,
  disableFutureDates = false,
  disablePastDates = false,
  isFullWidth = true,
  infoText,
  highlight,
}: DatePickerFieldProps<T>) {
  const showError = useShowError(touched, error)

  return (
    <FormFieldContainer>
      <FieldLabel
        name={name}
        label={label}
        required={required}
        infoText={infoText}
        error={showError ? error : undefined}
        highlight={highlight}
      />
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <DateSinglePicker
            value={(field.value as string | null | undefined) ?? undefined}
            onChange={(iso) => field.onChange(iso ?? null)}
            disableFutureDates={disableFutureDates}
            disablePastDates={disablePastDates}
            isFullWidth={isFullWidth}
            disabled={disabled}
            triggerClassName={cn(showError && 'border-destructive-foreground', highlight && HIGHLIGHT_CONTROL_CLASS)}
          />
        )}
      />
    </FormFieldContainer>
  )
}

// Checkbox Field
type CheckboxFieldProps<T extends FieldValues> = BaseFieldProps<T> & {
  containerClassName?: string
  rowClassName?: string
  labelWrapperClassName?: string
}

export function CheckboxField<T extends FieldValues>({
  name,
  label,
  control,
  error,
  disabled = false,
  required = false,
  touched,
  infoText,
  containerClassName,
  rowClassName,
  labelWrapperClassName,
  highlight,
}: CheckboxFieldProps<T>) {
  const showError = useShowError(touched, error)

  return (
    <FormFieldContainer className={cn('space-y-2', containerClassName)}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const isDisabled = disabled || field.disabled

          return (
            <div className={cn('flex items-center gap-2', rowClassName)}>
              <Checkbox
                id={String(name)}
                checked={!!field.value}
                onCheckedChange={(checked) => field.onChange(checked === true)}
                disabled={isDisabled}
                className={cn(showError && 'border-destructive-foreground', highlight && HIGHLIGHT_CONTROL_CLASS)}
              />
              <div className={cn('flex items-center gap-2', labelWrapperClassName)}>
                <Label
                  htmlFor={String(name)}
                  variant={isDisabled ? 'disabled' : 'enabled'}
                  className={cn('flex items-center', highlight && HIGHLIGHT_LABEL_CLASS)}
                >
                  {label}
                  {required && <RequiredIndicator />}
                </Label>
                {infoText && <ShowInfoPopover text={infoText} />}
                <ErrorIndicator error={showError ? error : undefined} />
              </div>
            </div>
          )
        }}
      />
    </FormFieldContainer>
  )
}

export const FormGrid = ({ children, cols = 2 }: { children: React.ReactNode; cols?: 1 | 2 }) => (
  <div className={cn('grid gap-4 *:min-w-0', cols === 2 && 'sm:grid-cols-2')}>{children}</div>
)

export const FormSection = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={cn('grid gap-4', className)}>{children}</div>
)
